/**
 * Inventory Management Types
 */

// ============= Enums =============

export enum IngredientCategory {
  MEAT = 'MEAT',
  FISH = 'FISH',
  VEGETABLES = 'VEGETABLES',
  FRUITS = 'FRUITS',
  DAIRY = 'DAIRY',
  GRAINS = 'GRAINS',
  SPICES = 'SPICES',
  OILS = 'OILS',
  BEVERAGES = 'BEVERAGES',
  PACKAGING = 'PACKAGING',
  CLEANING = 'CLEANING',
  OTHER = 'OTHER',
}

export enum StorageLocation {
  FRIDGE = 'FRIDGE',
  FREEZER = 'FREEZER',
  PANTRY = 'PANTRY',
  DRY_STORAGE = 'DRY_STORAGE',
  WINE_CELLAR = 'WINE_CELLAR',
  BAR = 'BAR',
  KITCHEN = 'KITCHEN',
}

export enum AllergenType {
  GLUTEN = 'GLUTEN',
  CRUSTACEANS = 'CRUSTACEANS',
  EGGS = 'EGGS',
  FISH = 'FISH',
  PEANUTS = 'PEANUTS',
  SOYBEANS = 'SOYBEANS',
  MILK = 'MILK',
  NUTS = 'NUTS',
  CELERY = 'CELERY',
  MUSTARD = 'MUSTARD',
  SESAME = 'SESAME',
  SULPHITES = 'SULPHITES',
  LUPIN = 'LUPIN',
  MOLLUSCS = 'MOLLUSCS',
}

export enum AlertType {
  LOW_STOCK = 'LOW_STOCK',
  OUT_OF_STOCK = 'OUT_OF_STOCK',
  EXPIRING_SOON = 'EXPIRING_SOON',
  EXPIRED = 'EXPIRED',
}

export enum AlertSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

export enum MovementType {
  PURCHASE = 'PURCHASE',
  SALE = 'SALE',
  ADJUSTMENT = 'ADJUSTMENT',
  WASTE = 'WASTE',
  RETURN = 'RETURN',
}

// ============= Main Types =============

export interface Ingredient {
  id: string;
  restaurantId: string;
  name: string;
  description?: string;
  category: IngredientCategory;
  unitOfMeasure: string;
  currentStock: number;
  minimumStock: number;
  reorderPoint?: number;
  unitCost: number;
  supplierName?: string;
  supplierContact?: string;
  storageLocation?: StorageLocation;
  shelfLifeDays?: number;
  allergen: boolean;
  allergenType?: AllergenType;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  // Computed fields
  isLowStock?: boolean;
  isCriticalStock?: boolean;
  totalValue?: number;
  productsUsingCount?: number;
}

export interface InventoryAlert {
  id: string;
  restaurantId: string;
  ingredientId: string;
  ingredientName?: string;
  alertType: AlertType;
  severity: AlertSeverity;
  message: string;
  isResolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  createdAt: string;
}

export interface InventoryMovement {
  id: string;
  restaurantId: string;
  ingredientId: string;
  movementType: MovementType;
  quantity: number;
  unitCost?: number;
  totalCost?: number;
  referenceType?: string;
  referenceId?: string;
  notes?: string;
  performedBy?: string;
  movementDate: string;
  createdAt: string;
}

export interface ProductRecipe {
  id: string;
  productId: string;
  ingredientId: string;
  quantity: number;
  isOptional: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryDashboard {
  totalIngredients: number;
  activeIngredients: number;
  lowStockCount: number;
  outOfStockCount: number;
  criticalAlertsCount: number;
  totalInventoryValue: number;
  lowStockIngredients: Ingredient[];
  criticalStockIngredients: Ingredient[];
  recentAlerts: InventoryAlert[];
}

// ============= Request/Response Types =============

export interface StockAdjustmentRequest {
  quantity: number;
  notes?: string;
}

export interface PurchaseRequest {
  quantity: number;
  unitCost?: number;
  supplierOrderId?: string;
}

export interface FoodCostResponse {
  productId: string;
  foodCost: number;
  currency: string;
}

// ============= UI Helper Types =============

export const CATEGORY_LABELS: Record<IngredientCategory, string> = {
  [IngredientCategory.MEAT]: '🥩 Carne',
  [IngredientCategory.FISH]: '🐟 Pesce',
  [IngredientCategory.VEGETABLES]: '🥬 Verdure',
  [IngredientCategory.FRUITS]: '🍎 Frutta',
  [IngredientCategory.DAIRY]: '🧀 Latticini',
  [IngredientCategory.GRAINS]: '🌾 Cereali',
  [IngredientCategory.SPICES]: '🌶️ Spezie',
  [IngredientCategory.OILS]: '🫒 Oli',
  [IngredientCategory.BEVERAGES]: '🥤 Bevande',
  [IngredientCategory.PACKAGING]: '📦 Packaging',
  [IngredientCategory.CLEANING]: '🧹 Pulizia',
  [IngredientCategory.OTHER]: '📋 Altro',
};

export const STORAGE_LABELS: Record<StorageLocation, string> = {
  [StorageLocation.FRIDGE]: '❄️ Frigo',
  [StorageLocation.FREEZER]: '🧊 Freezer',
  [StorageLocation.PANTRY]: '🚪 Dispensa',
  [StorageLocation.DRY_STORAGE]: '📦 Magazzino',
  [StorageLocation.WINE_CELLAR]: '🍷 Cantina',
  [StorageLocation.BAR]: '🍸 Bar',
  [StorageLocation.KITCHEN]: '👨‍🍳 Cucina',
};

export const ALERT_SEVERITY_COLORS: Record<AlertSeverity, string> = {
  [AlertSeverity.LOW]: 'bg-blue-100 text-blue-800 border-blue-200',
  [AlertSeverity.MEDIUM]: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  [AlertSeverity.HIGH]: 'bg-orange-100 text-orange-800 border-orange-200',
  [AlertSeverity.CRITICAL]: 'bg-red-100 text-red-800 border-red-200',
};

export const MOVEMENT_TYPE_LABELS: Record<MovementType, string> = {
  [MovementType.PURCHASE]: '📥 Acquisto',
  [MovementType.SALE]: '📤 Vendita',
  [MovementType.ADJUSTMENT]: '⚙️ Correzione',
  [MovementType.WASTE]: '🗑️ Spreco',
  [MovementType.RETURN]: '↩️ Reso',
};

export const MOVEMENT_TYPE_COLORS: Record<MovementType, string> = {
  [MovementType.PURCHASE]: 'text-green-600 dark:text-green-300 bg-green-50 dark:bg-green-900/30 dark:ring-1 dark:ring-green-500/30',
  [MovementType.SALE]: 'text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 dark:ring-1 dark:ring-blue-500/30',
  [MovementType.ADJUSTMENT]: 'text-purple-600 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 dark:ring-1 dark:ring-purple-500/30',
  [MovementType.WASTE]: 'text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/30 dark:ring-1 dark:ring-red-500/30',
  [MovementType.RETURN]: 'text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/30 dark:ring-1 dark:ring-orange-500/30',
};
